import { LeftSidebarDirectory } from "../../types";
import { DIRECTORY_HEADER_HEIGHT } from "./constants";
import { DirectoryState, getFlexGrow } from "./types";

/**
 * Calculate the actual pixel height of each directory based on flex-grow values.
 *
 * Collapsed directories take up DIRECTORY_HEADER_HEIGHT (just the header).
 * Open directories share the remaining space proportionally by their flexGrow.
 *
 * @param directories - Record of directory states
 * @param containerHeight - Total height of the container in pixels
 * @returns Object mapping directory names to pixel heights
 */
export function calculateDirectoryHeights(
  directories: Record<LeftSidebarDirectory, DirectoryState>,
  containerHeight: number
): Record<LeftSidebarDirectory, number> {
  // Calculate total flex-grow (only from open directories)
  const totalFlexGrow = Object.values(directories).reduce(
    (sum, dir) => sum + getFlexGrow(dir),
    0
  );

  // Count collapsed directories
  const collapsedCount = Object.values(directories).filter(
    dir => !dir.isOpen
  ).length;

  // Calculate space available for open directories
  const spaceForCollapsed = collapsedCount * DIRECTORY_HEADER_HEIGHT;
  const availableForOpen = containerHeight - spaceForCollapsed;

  const result: Record<string, number> = {};

  if (totalFlexGrow === 0) {
    // All directories are collapsed - each gets just header height
    Object.keys(directories).forEach(key => {
      result[key] = DIRECTORY_HEADER_HEIGHT;
    });
    return result;
  }

  // Calculate height for each directory
  Object.entries(directories).forEach(([key, state]) => {
    if (state.isOpen) {
      // Open directory: proportional share of available space
      result[key] = (state.flexGrow / totalFlexGrow) * availableForOpen;
    } else {
      // Collapsed directory: just the header height
      result[key] = DIRECTORY_HEADER_HEIGHT;
    }
  });

  return result;
}
